import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IFacet, IMakesFacets } from '@ncg/data';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { filterParams } from '../../+state/products/products.actions';
import { ProductsFacade } from '../../+state/products/products.facade';
import { ScrollService } from '../../core/scroll.service';
import { SettingsService } from '../../core/settings.service';
import { TrackingService } from '../../core/tracking.service';
import { ProductService } from '../../products/product.service';
import { menuBarAnimation } from '../../utils/animations/filter.animation';

@Component({
    selector: 'ncg-filter-tab-content',
    template: `
        <nav class="navbar filter-navbar" #navRef *ngIf="facetItem">
            <button
                #btnRef
                *ngIf="facetItem.bodyTypes"
                class="button is-filter"
                [ngClass]="{ 'is-active-bar': currentItem === 1 }"
                (click)="setItem(1, btnRef)"
            >
                <span>{{ 'filter_spot.tab_label_types' | translate }}</span>
            </button>
            <button
                #btnRef
                *ngIf="facetItem.series"
                class="button is-filter"
                [ngClass]="{ 'is-active-bar': currentItem === 2 }"
                (click)="setItem(2, btnRef)"
            >
                <span>{{ 'filter_spot.tab_label_series' | translate }}</span>
            </button>
            <div
                [@menuBar]="{
                    value: state,
                    params: {
                        scaleToPercentage: scaleToPercentage,
                        scalePercentage: scalePercentage,
                        fromX: fromX + 'px',
                        toX: toX + 'px'
                    }
                }"
                #activeBarRef
                class="active-bar"
            ></div>
        </nav>

        <!-- BODY TYPE -->
        <div *ngIf="currentItem === 1 && facetItem?.bodyTypes" class="filter-tab-content__items">
            <ng-container *ngFor="let facet of facetItem?.bodyTypes; trackBy: trackByFn">
                <a
                    *ngIf="!isPartOfFilterPage"
                    [routerLink]="[linkToUsedCars]"
                    [queryParams]="{ 'bodytypes.keyword': facet.key, 'make.keyword': makeKey }"
                    class="filter-tab-content__item level-item"
                >
                    <div class="has-text-centered">
                        <ncg-filter-type-icons [facet]="facet"></ncg-filter-type-icons>
                    </div>
                </a>
                <button
                    *ngIf="isPartOfFilterPage"
                    type="button"
                    class="button filter-tab-content__item level-item"
                    [ngClass]="{ 'filter-tab-content__item--active': facet?.selected, 'is-fetching': currentKey === facet.key && isFetching }"
                    (click)="updateFilterQuery(facet, 'bodytypes.keyword')"
                >
                    <div class="has-text-centered">
                        <ncg-filter-type-icons [facet]="facet"></ncg-filter-type-icons>
                    </div>
                </button>
            </ng-container>
        </div>

        <!-- SERIES -->
        <div *ngIf="currentItem === 2 && facetItem?.series" class="filter-tab-content__items">
            <ng-container *ngFor="let facet of facetItem?.series; trackBy: trackByFn">
                <a
                    *ngIf="!isPartOfFilterPage"
                    ncgLoadIn
                    [routerLink]="[linkToUsedCars]"
                    [queryParams]="{ 'series.keyword': facet.key, 'make.keyword': makeKey }"
                    class="filter-tab-content__item level-item"
                >
                    <span class="has-text-black">{{ 'filter_spot.label_facet_series' | translate }} {{ facet.key }}</span>
                    <span class="has-text-grey-dark">&nbsp;({{ facet.count }})</span>
                </a>
                <button
                    *ngIf="isPartOfFilterPage"
                    type="button"
                    class="button filter-tab-content__item level-item"
                    [ngClass]="{ 'filter-tab-content__item--active': facet?.selected, 'is-fetching': currentKey === facet.key && isFetching }"
                    (click)="updateFilterQuery(facet, 'series.keyword')"
                >
                    <span class="has-text-black">{{ 'filter_spot.label_facet_series' | translate }} {{ facet.key }} </span>
                    <span class="has-text-grey-dark">&nbsp;({{ facet.count }})</span>
                </button>
            </ng-container>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [menuBarAnimation],
})
export class FilterTabContentComponent implements OnInit, OnDestroy {
    @ViewChild('navRef') navRef: ElementRef;
    @ViewChild('btnRef') btnRef: any;
    @ViewChild('activeBarRef') activeBarRef: ElementRef;
    @Input() makeKey: string;
    @Input() isPartOfFilterPage = true;
    @Input() set isVisible(value: boolean) {
        if (value && !this.scalePercentage) {
            this.initializeFilterButton();
        }
    }
    @Input() facetItem?: IMakesFacets;
    currentItem = 1;
    state: 'initial' | 'move' = 'initial';
    scaleToPercentage = 0;
    scalePercentage = 0;
    prevButton?: HTMLButtonElement;
    toButton: HTMLButtonElement;
    fromX = 0;
    toX = 0;
    linkToUsedCars = '';
    direction: 'leftToRight' | 'rightToLeft' = 'leftToRight';
    unsubscribe = new Subject<void>();
    isFetching = false;
    currentKey?: string | null;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly settingsService: SettingsService,
        private readonly productService: ProductService,
        private readonly trackingService: TrackingService,
        private readonly scrollService: ScrollService,
        public readonly productsFacade: ProductsFacade
    ) {}

    ngOnInit() {
        if (!this.isPartOfFilterPage && this.makeKey) {
            this.productService
                .filter({ 'from': '0', 'take': '0', 'make.keyword': this.makeKey })
                .pipe(take(1), takeUntil(this.unsubscribe))
                .subscribe((data) => {
                    this.facetItem = {
                        'make.keyword': this.makeKey,
                        'bodyTypes': data?.facets?.['bodytypes.keyword'],
                        'series': this.makeKey.toLowerCase() === 'bmw' ? data?.facets?.['series.keyword'] : undefined,
                    };
                    this.cd.markForCheck();
                });
        }

        this.getUsedCarLink();
        setTimeout(() => {
            this.initializeFilterButton();
        });

        this.getIsFetching();
    }

    updateFilterQuery(facet: IFacet, facetKey?: string | null) {
        this.currentKey = facet.key;
        if (facet.key && facetKey) {
            this.productsFacade.dispatch(
                filterParams({
                    filterParams: { [facetKey]: [facet.key] },
                })
            );

            if (!facet.selected) {
                this.trackingService.trackFilter(facet.key, facetKey);
            }
        }
    }

    getUsedCarLink() {
        this.settingsService
            .get()
            .pipe(take(1), takeUntil(this.unsubscribe))
            .subscribe((settings) => {
                this.linkToUsedCars = settings.usedCarsLink.url;
            });
    }

    initializeFilterButton() {
        if (this.btnRef && this.btnRef.nativeElement.clientWidth > 0) {
            this.toButton = this.btnRef.nativeElement;
            this.setItem(this.currentItem, this.toButton);
        }
    }

    setItem(index: number, btnElement?: HTMLButtonElement): void {
        this.currentItem = index;
        if (this.navRef && btnElement) {
            this.prevButton = this.toButton;
            this.toButton = btnElement;
            this.scrollToButtonElement();
            this.animateActiveBar();
        }
        this.cd.markForCheck();
    }

    getIsFetching() {
        this.productsFacade.isFetching$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.isFetching = data;
            if (!this.isFetching) {
                this.currentKey = undefined;
            }
            this.cd.markForCheck();
        });
    }

    scrollToButtonElement() {
        let scrollNumber;
        const itemOffsetLeft = this.toButton.offsetLeft;
        const itemWidth = this.toButton.clientWidth;
        const nav: HTMLElement = this.navRef.nativeElement;
        const navWidth = nav.offsetWidth;
        const navOffsetLeft = nav.scrollLeft;
        const btnPadding = 40;

        if (itemWidth + itemOffsetLeft > navWidth + navOffsetLeft) {
            scrollNumber = itemWidth + itemOffsetLeft - navWidth;
            scrollNumber = scrollNumber + btnPadding;
        } else if (navOffsetLeft > itemOffsetLeft) {
            scrollNumber = itemOffsetLeft;
            scrollNumber = scrollNumber - btnPadding;
        } else {
            return;
        }

        this.scrollService.scrollToPosition({ left: scrollNumber }, nav);
    }

    animateActiveBar(isResize?: boolean) {
        if (this.navRef && this.navRef.nativeElement && this.activeBarRef.nativeElement) {
            const nav: HTMLElement = this.navRef.nativeElement;
            const btnStyle = window.getComputedStyle(this.toButton);
            const btnPl = btnStyle.paddingLeft ? Number(btnStyle.paddingLeft.replace('px', '')) : 0;
            const btnPr = btnStyle.paddingRight ? Number(btnStyle.paddingRight.replace('px', '')) : 0;
            const padding = btnPl + btnPr;
            const width = this.toButton.clientWidth - padding;
            this.scaleToPercentage = width / nav.offsetWidth;
            this.toX = this.toButton.offsetLeft + btnPl;

            // Only set new width an X coordinates when resizing window
            if (isResize) {
                return;
            }

            if (this.prevButton) {
                const prevBtnStyle = window.getComputedStyle(this.prevButton);
                const prevBtnPl = prevBtnStyle.paddingLeft ? Number(prevBtnStyle.paddingLeft.replace('px', '')) : 0;
                const prevBtnPr = prevBtnStyle.paddingRight ? Number(prevBtnStyle.paddingRight.replace('px', '')) : 0;
                const prevPadding = prevBtnPl + prevBtnPr;
                const prevWidth = this.prevButton.clientWidth - prevPadding;

                this.direction = this.prevButton.offsetLeft < this.toButton.offsetLeft ? 'leftToRight' : 'rightToLeft';

                if (this.direction === 'leftToRight') {
                    this.fromX = this.prevButton.offsetLeft + prevBtnPl;
                    this.scalePercentage = (this.toButton.offsetLeft + btnPl + width - (this.prevButton.offsetLeft + prevBtnPl)) / nav.offsetWidth;
                } else {
                    this.fromX = this.toButton.offsetLeft + btnPl;
                    this.scalePercentage =
                        (this.prevButton.offsetLeft + prevBtnPl + prevWidth - (this.toButton.offsetLeft + btnPl)) / nav.offsetWidth;
                }
            } else {
                this.fromX = 0;
                this.scalePercentage = this.toX / nav.offsetWidth + this.scaleToPercentage;
            }

            this.state = this.state === 'initial' ? 'move' : 'initial';
            this.cd.detectChanges();
        }
    }

    trackByFn(index: number, item: any) {
        return item.key || index;
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
